import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useHaptics from '../../hooks/useHaptics';
import SEOHead from '../SEO/SEOHead';
import { SOCIALS } from '../../config';
import {
  Typography,
  Button,
  Container,
  Grid,
  Card,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Slide,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Rating,
  alpha,
} from '@mui/material';
import {
  X,
  ArrowRight,
  Zap,
  Shield,
  TrendingUp,
  Users,
  Receipt,
  PieChart,
  Check,
  Moon,
  Sun,
  UserPlus,
  Link2,
  ScanLine,
  Target,
  ChevronDown,
  Sparkles,
  HelpCircle,
} from 'lucide-react';

// Lazy-loaded legal dialogs
const LegalDialogs = lazy(() => import('./LegalDialogs'));

// Theme configuration
const createAppTheme = (mode = 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    secondary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    background: {
      default: mode === 'dark' ? '#0f172a' : '#ffffff',
      paper: mode === 'dark' ? '#1e293b' : '#f8fafc',
    },
    text: {
      primary: mode === 'dark' ? '#ffffff' : '#1e293b',
      secondary: mode === 'dark' ? '#cbd5e1' : '#64748b',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 800, lineHeight: 1.1 },
    h2: { fontWeight: 600, lineHeight: 1.2 },
    h3: { fontWeight: 600, lineHeight: 1.3 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
        },
      },
    },
  },
});

// CSS-only animated dashboard mockup inside phone frame (desktop only)
const HeroDashboardMockup = ({ themeMode }) => {
  const isDark = themeMode === 'dark';
  const cardBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const borderClr = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  return (
    <Box
      sx={{
        position: 'relative',
        width: { md: 300 },
        height: { md: 600 },
        margin: '0 auto',
        animation: 'float 6s ease-in-out infinite',
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-16px)' },
        },
      }}
    >
      <Box sx={{
        position: 'absolute', inset: -40,
        background: 'radial-gradient(circle, rgba(16,185,129,0.2) 0%, transparent 70%)',
        filter: 'blur(40px)', borderRadius: '50%', pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'relative', width: '100%', height: '100%',
        borderRadius: '36px', overflow: 'hidden',
        border: '6px solid', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
        background: isDark ? '#0f172a' : '#ffffff',
        boxShadow: isDark
          ? '0 30px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)'
          : '0 30px 80px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8)',
      }}>
        <Box sx={{ width: 80, height: 22, borderRadius: 20, background: '#000', margin: '8px auto 12px' }} />
        <Box sx={{ px: 1.5 }}>
          <Box sx={{
            p: 1.5, mb: 1.2, borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(59,130,246,0.15))',
            border: `1px solid ${borderClr}`,
          }}>
            <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary', mb: 0.3 }}>Total Balance</Typography>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: 'primary.main' }}>€4,892.50</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
              <TrendingUp size={10} color="#10b981" />
              <Typography sx={{ fontSize: '0.5rem', color: '#10b981', fontWeight: 600 }}>+12.4% this month</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'flex-end', height: 60, mb: 1.2 }}>
            {[40, 65, 45, 80, 55, 70, 35].map((h, i) => (
              <Box key={i} sx={{
                flex: 1, height: `${h}%`, borderRadius: '4px 4px 0 0',
                background: i === 3
                  ? 'linear-gradient(180deg, #10b981 0%, #3b82f6 100%)'
                  : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                animation: `barGrow 0.6s ease ${i * 0.08}s both`,
                '@keyframes barGrow': { from: { transform: 'scaleY(0)' }, to: { transform: 'scaleY(1)' } },
                transformOrigin: 'bottom',
              }} />
            ))}
          </Box>
          {[
            { emoji: '🛒', label: 'Groceries', amount: '-€67.30', color: '#ef4444' },
            { emoji: '💼', label: 'Salary', amount: '+€3,200', color: '#10b981' },
            { emoji: '🏠', label: 'Rent', amount: '-€850.00', color: '#ef4444' },
            { emoji: '☕', label: 'Coffee', amount: '-€4.50', color: '#f59e0b' },
          ].map((tx, i) => (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              py: 0.6, px: 1, mb: 0.5, borderRadius: 1.5,
              background: cardBg, border: `1px solid ${borderClr}`,
              animation: `slideRight 0.5s ease ${0.3 + i * 0.1}s both`,
              '@keyframes slideRight': {
                from: { opacity: 0, transform: 'translateX(-12px)' },
                to: { opacity: 1, transform: 'translateX(0)' },
              },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Typography sx={{ fontSize: '0.75rem' }}>{tx.emoji}</Typography>
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 500 }}>{tx.label}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: tx.color }}>{tx.amount}</Typography>
            </Box>
          ))}
          <Box sx={{ mt: 1.2, p: 1.2, borderRadius: 2, background: cardBg, border: `1px solid ${borderClr}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 600 }}>Monthly Budget</Typography>
              <Typography sx={{ fontSize: '0.5rem', color: 'text.secondary' }}>68%</Typography>
            </Box>
            <Box sx={{ height: 5, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              <Box sx={{
                width: '68%', height: '100%', borderRadius: 3,
                background: 'linear-gradient(90deg, #10b981, #3b82f6)',
                animation: 'progressFill 1.5s ease 0.5s both',
                '@keyframes progressFill': { from: { width: '0%' }, to: { width: '68%' } },
              }} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Animated count-up number — counts from 0 to target when visible
const AnimatedCounter = ({ value, duration = 1200, sx = {} }) => {
  const ref = useRef(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // Auto-extract prefix/suffix from value (e.g. "€0" → prefix="€", "100%" → suffix="%")
  const str = String(value);
  const match = str.match(/^([^0-9]*)([0-9.]+)([^0-9]*)$/);
  const prefix = match?.[1] || '';
  const numericStr = match?.[2] || '0';
  const suffix = match?.[3] || '';
  const num = parseFloat(numericStr) || 0;
  const [display, setDisplay] = useState(str);

  useEffect(() => {
    if (prefersReducedMotion || num === 0) { setDisplay(str); return; }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplay(prefix + String(Math.round(num * eased)) + suffix);
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [str, prefix, suffix, num, duration, prefersReducedMotion]);

  return <Typography ref={ref} sx={sx}>{display}</Typography>;
};

// Scroll-triggered section wrapper with reduced-motion support
const AnimatedSection = ({ children, delay = 0, sx = {} }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    if (prefersReducedMotion) { setVisible(true); return; }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: prefersReducedMotion ? 'none' : `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const LandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { impact, selectionClick } = useHaptics();
  const [themeMode, setThemeMode] = useState('dark');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [cookieDialogOpen, setCookieDialogOpen] = useState(false);
  const [policyData, setPolicyData] = useState({ privacy: null, cookie: null });
  const [scrollY, setScrollY] = useState(0);
  const [faqExpanded, setFaqExpanded] = useState(false);

  const isMobile = useMediaQuery('(max-width:900px)');
  const isXSmall = useMediaQuery('(max-width:380px)');
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const theme = createAppTheme(themeMode);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const pos = window.scrollY;
      setScrolled(pos > 50);
      setShowFloatingCta(pos > (window.innerHeight * 0.7));
      if (!prefersReducedMotion) setScrollY(pos);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prefersReducedMotion]);

  useEffect(() => {
    const type = privacyDialogOpen ? 'privacy' : cookieDialogOpen ? 'cookie' : null;
    if (!type || policyData[type]) return;
    const apiBase = process.env.REACT_APP_API_URL || '';
    fetch(`${apiBase}/api/policy/${type}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.content) setPolicyData(prev => ({ ...prev, [type]: data })); })
      .catch(() => {});
  }, [privacyDialogOpen, cookieDialogOpen]);

  const toggleTheme = () => { selectionClick(); setThemeMode((prev) => (prev === 'dark' ? 'light' : 'dark')); };
  const handleStartFreeTrial = () => { impact('Medium'); navigate('/register'); };
  const handleSignIn = () => { impact('Light'); navigate('/login'); };
  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setMobileDrawerOpen(false);
  };

  // Reusable button micro-interaction sx
  const ctaPress = {
    '&:active': { transform: 'scale(0.97)' },
    '&:hover': { transform: { md: 'translateY(-2px)' } },
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  // Parallax helper — returns translateY for given speed factor
  const parallax = (speed = 0.3) =>
    prefersReducedMotion ? 'translate(0,0)' : `translate(0, ${scrollY * speed}px)`;

  const glassStyle = {
    background: themeMode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    border: '1px solid',
    borderColor: themeMode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.1)',
  };

  const features = [
    { key: 'smartDashboard', icon: <PieChart size={28} />, gradient: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)' },
    { key: 'aiCoach', icon: <Zap size={28} />, gradient: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)' },
    { key: 'security', icon: <Shield size={28} />, gradient: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)' },
    { key: 'billSplitting', icon: <Users size={28} />, gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' },
    { key: 'receiptScan', icon: <Receipt size={28} />, gradient: 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)' },
    { key: 'budgetGoals', icon: <Target size={28} />, gradient: 'linear-gradient(135deg, #10b981 0%, #f59e0b 100%)' },
  ];

  const steps = [
    { key: 'signup', icon: <UserPlus size={40} />, number: 1 },
    { key: 'connect', icon: <ScanLine size={40} />, number: 2 },
    { key: 'save', icon: <Target size={40} />, number: 3 },
  ];

  const plans = [
    { key: 'free', popular: false },
    { key: 'standard', popular: false },
    { key: 'premium', popular: true },
    { key: 'family', popular: false },
    { key: 'business', popular: false },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SEOHead
        title="Budget without the bank login | SoldiKeeper"
        description="Privacy-first AI budgeting for Europe. Track euros with receipt OCR and SplitSmart — no bank login, GDPR, free forever plan. Start at soldikeeper.com."
        canonical="/"
        keywords="budget without bank login, privacy-first finance app, no bank login budget app, GDPR expense tracker, AI budget app Europe, SplitSmart, free budget app, AI receipt scanner"
      />
      <Box sx={{ minHeight: '100vh', overflowX: 'hidden' }}>

        {/* ═══════════════════════════════════════════════════════════════
            NAVIGATION — Floating island header
        ═══════════════════════════════════════════════════════════════ */}
        <Box sx={{
          position: 'fixed',
          top: { xs: 10, md: 14 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1200,
          width: { xs: 'calc(100% - 24px)', md: 'min(880px, 90%)' },
          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 1.5, md: 2.5 },
            py: { xs: 0.6, md: 0.7 },
            borderRadius: { xs: 3, md: 4 },
            background: themeMode === 'dark'
              ? (scrolled ? 'rgba(15, 23, 42, 0.82)' : 'rgba(15, 23, 42, 0.5)')
              : (scrolled ? 'rgba(255, 255, 255, 0.82)' : 'rgba(255, 255, 255, 0.4)'),
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: themeMode === 'dark'
              ? (scrolled ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.06)')
              : (scrolled ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.04)'),
            boxShadow: scrolled
              ? '0 8px 32px rgba(0,0,0,0.12)'
              : '0 4px 16px rgba(0,0,0,0.06)',
            transition: 'all 0.35s ease',
          }}>
            {/* Left: Logo + brand */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.8, cursor: 'pointer', flexShrink: 0 }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <Box
                component="img"
                src="/assets/images/logo.png"
                alt="SoldiKeeper"
                sx={{ height: { xs: 26, md: 30 }, width: 'auto' }}
              />
              <Typography sx={{
                fontWeight: 700,
                fontSize: { xs: '0.88rem', md: '0.95rem' },
                color: 'text.primary',
                letterSpacing: '-0.01em',
              }}>
                SoldiKeeper
              </Typography>
            </Box>

            {/* Right side */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, md: 0.5 } }}>
              {/* Desktop-only nav links */}
              {!isMobile && ['features', 'pricing'].map((section) => (
                <Button
                  key={section}
                  color="inherit"
                  onClick={() => scrollToSection(section)}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.82rem',
                    textTransform: 'none',
                    color: 'text.secondary',
                    px: 1.5,
                    minHeight: 32,
                    '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
                  }}
                >
                  {t(`navigation.${section}`)}
                </Button>
              ))}

              {/* Compact flag-only language picker */}
              <IconButton
                size="small"
                onClick={(e) => {
                  const langs = ['en', 'es', 'fr', 'de', 'it', 'pt'];
                  const flags = { en: '🇺🇸', es: '🇪🇸', fr: '🇫🇷', de: '🇩🇪', it: '🇮🇹', pt: '🇵🇹' };
                  const currentIdx = langs.indexOf(i18n.language);
                  const nextLang = langs[(currentIdx + 1) % langs.length];
                  i18n.changeLanguage(nextLang);
                  localStorage.setItem('soldikeeper-language', nextLang);
                }}
                sx={{
                  fontSize: '1rem',
                  p: 0.6,
                  color: 'text.secondary',
                }}
              >
                <span style={{ fontSize: '16px', lineHeight: 1 }}>
                  {{ en: '🇺🇸', es: '🇪🇸', fr: '🇫🇷', de: '🇩🇪', it: '🇮🇹', pt: '🇵🇹' }[i18n.language] || '🌐'}
                </span>
              </IconButton>

              {/* Theme toggle */}
              <IconButton onClick={toggleTheme} size="small" sx={{ p: 0.6, color: 'text.secondary' }}>
                {themeMode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </IconButton>

              {/* Sign in — text on mobile, button on desktop */}
              {isMobile ? (
                <Typography
                  component="span"
                  onClick={handleSignIn}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: '#10b981',
                    cursor: 'pointer',
                    pl: 0.5,
                    '&:active': { opacity: 0.7 },
                  }}
                >
                  {t('navigation.signIn')}
                </Typography>
              ) : (
                <>
                  <Button
                    onClick={handleSignIn}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      textTransform: 'none',
                      color: 'text.primary',
                      minHeight: 32,
                      px: 1.5,
                    }}
                  >
                    {t('navigation.signIn')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleStartFreeTrial}
                    disableElevation
                    size="small"
                    sx={{
                      py: 0.5,
                      px: 2,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      bgcolor: '#10b981',
                      minHeight: 32,
                      '&:hover': { bgcolor: '#059669' },
                    }}
                  >
                    {t('landing.hero.ctaPrimary')}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            HERO — MOBILE: Full-viewport immersive app experience
        ═══════════════════════════════════════════════════════════════ */}
        <Box
          ref={heroRef}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            background: themeMode === 'dark'
              ? 'radial-gradient(circle at 20% 30%, rgba(16, 185, 129, 0.18) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)'
              : 'radial-gradient(circle at 20% 30%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
          }}
        >
          {/* ——— MOBILE HERO ——— */}
          <Box sx={{
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '100dvh',
            textAlign: 'center',
            pt: 9,
            pb: 3,
            px: 3,
          }}>
            {/* Animated background orbs with parallax */}
            <Box sx={{
              position: 'absolute', top: '5%', left: '-20%',
              width: 300, height: 300, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(16,185,129,0.15) 0%, transparent 70%)',
              filter: 'blur(60px)', pointerEvents: 'none',
              transform: parallax(-0.08),
              animation: prefersReducedMotion ? 'none' : 'orbFloat 8s ease-in-out infinite',
              '@keyframes orbFloat': {
                '0%, 100%': { transform: 'translate(0, 0)' },
                '50%': { transform: 'translate(30px, -20px)' },
              },
            }} />
            <Box sx={{
              position: 'absolute', bottom: '15%', right: '-15%',
              width: 250, height: 250, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
              filter: 'blur(60px)', pointerEvents: 'none',
              transform: parallax(0.06),
              animation: prefersReducedMotion ? 'none' : 'orbFloat2 10s ease-in-out infinite',
              '@keyframes orbFloat2': {
                '0%, 100%': { transform: 'translate(0, 0)' },
                '50%': { transform: 'translate(-25px, 15px)' },
              },
            }} />

            {/* Top content */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', position: 'relative', zIndex: 1 }}>
              {/* Logo — large, with glow */}
              <Box
                component="img"
                src="/assets/images/logo_512.png"
                alt="SoldiKeeper"
                sx={{
                  width: isXSmall ? 80 : 100,
                  height: isXSmall ? 80 : 100,
                  mb: 2,
                  filter: `drop-shadow(0 0 ${themeMode === 'dark' ? '40px' : '20px'} rgba(16, 185, 129, ${themeMode === 'dark' ? 0.5 : 0.3}))`,
                  animation: prefersReducedMotion ? 'none' : 'logoBreath 3s ease-in-out infinite',
                  '@keyframes logoBreath': {
                    '0%, 100%': { transform: 'scale(1)', filter: `drop-shadow(0 0 30px rgba(16, 185, 129, 0.4))` },
                    '50%': { transform: 'scale(1.05)', filter: `drop-shadow(0 0 50px rgba(16, 185, 129, 0.6))` },
                  },
                }}
              />

              {/* Badge */}
              <Chip
                icon={<Sparkles size={14} />}
                label={t('landing.hero.badge')}
                size="small"
                sx={{
                  background: alpha('#ffffff', 0.08),
                  color: 'rgba(255,255,255,0.7)',
                  fontWeight: 500,
                  fontSize: '0.72rem',
                  height: 30,
                  mb: 2.5,
                  border: '1px solid rgba(255,255,255,0.15)',
                  boxShadow: 'none',
                  '& .MuiChip-icon': { color: 'rgba(255,255,255,0.7)', ml: '6px' },
                }}
              />

              {/* Headline — big and bold */}
              <Typography
                component="h1"
                sx={{
                  fontSize: isXSmall ? '1.75rem' : '2.1rem',
                  fontWeight: 800,
                  lineHeight: 1.15,
                  mb: 2.5,
                  letterSpacing: '-0.02em',
                }}
              >
                {t('landing.hero.title')}{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(-45deg, #10b981, #3b82f6, #8b5cf6, #10b981)',
                    backgroundSize: '300% 300%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: prefersReducedMotion ? 'none' : 'gradient 4s ease infinite',
                    '@keyframes gradient': {
                      '0%, 100%': { backgroundPosition: '0% 50%' },
                      '50%': { backgroundPosition: '100% 50%' },
                    },
                  }}
                >
                  {t('landing.hero.titleHighlight')}
                </Box>
              </Typography>

              {/* Social proof — 3 animated counters */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: isXSmall ? 2.5 : 4,
                mb: 2,
                width: '100%',
              }}>
                {[
                  { value: '6', label: t('landing.stats.languages'), color: '#10b981' },
                  { value: '€0', label: t('landing.stats.freeForever'), color: '#8b5cf6' },
                  { value: '100%', label: t('landing.stats.gdprCompliant'), color: '#3b82f6' },
                ].map((stat, i) => (
                  <Box key={i} sx={{ textAlign: 'center' }}>
                    <AnimatedCounter
                      value={stat.value}
                      sx={{
                        fontSize: isXSmall ? '1.2rem' : '1.4rem',
                        fontWeight: 800,
                        color: stat.color,
                        lineHeight: 1.1,
                      }}
                    />
                    <Typography sx={{
                      fontSize: '0.68rem',
                      color: 'text.secondary',
                      mt: 0.3,
                      lineHeight: 1.2,
                    }}>
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Spacer */}
            <Box sx={{ flex: 1, minHeight: 16 }} />

            {/* CTA — segmented pill: two actions fused into one shape */}
            <Box sx={{ width: '100%', maxWidth: 320, position: 'relative', zIndex: 1 }}>
              {/* Outer container — the unified pill */}
              <Box sx={{
                display: 'flex',
                borderRadius: 50,
                overflow: 'hidden',
                border: '2px solid',
                borderColor: '#10b981',
                position: 'relative',
                // animated glow pulse
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.25)',
                animation: prefersReducedMotion ? 'none' : 'ctaGlow 3s ease-in-out infinite',
                '@keyframes ctaGlow': {
                  '0%, 100%': { boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)' },
                  '50%': { boxShadow: '0 0 35px rgba(16, 185, 129, 0.4)' },
                },
              }}>
                {/* Left: Get Started (filled) */}
                <Box
                  onClick={handleStartFreeTrial}
                  sx={{
                    flex: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.8,
                    py: 1.5,
                    px: 2,
                    bgcolor: '#10b981',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    '&:active': { bgcolor: '#059669' },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'inherit', whiteSpace: 'nowrap' }}>
                    {t('landing.hero.ctaPrimary')}
                  </Typography>
                  <ArrowRight size={18} />
                </Box>

                {/* Divider line */}
                <Box sx={{ width: '1px', bgcolor: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />

                {/* Right: Sign In (transparent) */}
                <Box
                  onClick={handleSignIn}
                  sx={{
                    flex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 1.5,
                    px: 2,
                    bgcolor: themeMode === 'dark' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.05)',
                    color: '#10b981',
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    '&:active': { bgcolor: themeMode === 'dark' ? 'rgba(16, 185, 129, 0.18)' : 'rgba(16, 185, 129, 0.12)' },
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: 'inherit', whiteSpace: 'nowrap' }}>
                    {t('navigation.signIn')}
                  </Typography>
                </Box>
              </Box>

              {/* Google Play badge — centered below */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Box
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.soldikeeper.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.6,
                    borderRadius: 50,
                    bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    border: '1px solid',
                    borderColor: themeMode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                    textDecoration: 'none',
                    '&:active': { opacity: 0.7 },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box component="img" src="/assets/images/google-play-badge.svg" alt="Google Play" sx={{ height: 20, width: 'auto' }} />
                </Box>
              </Box>
            </Box>

            {/* Scroll hint */}
            <Box sx={{
              mt: 2,
              animation: prefersReducedMotion ? 'none' : 'bounce 2s ease-in-out infinite',
              '@keyframes bounce': {
                '0%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
                '50%': { transform: 'translateY(6px)', opacity: 0.8 },
              },
            }}>
              <ChevronDown size={24} color={themeMode === 'dark' ? '#cbd5e1' : '#64748b'} />
            </Box>
          </Box>

          {/* ——— DESKTOP HERO ——— */}
          <Box sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            px: { md: '5vw', lg: '6vw' },
            gap: { md: 6, lg: 8 },
            pt: 16,
            pb: 12,
          }}>
            <Box sx={{ flex: '0 0 48%' }}>
              <Chip
                label={t('landing.hero.badge')}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                  color: 'white', fontWeight: 600, fontSize: '0.8rem', height: 32, mb: 3,
                }}
              />
              <Typography
                component="h1"
                sx={{ fontSize: 'clamp(1.6rem, 2.7vw, 3.5rem)', fontWeight: 800, mb: 2, lineHeight: 1.15 }}
              >
                {t('landing.hero.title')}{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(-45deg, #10b981, #3b82f6, #8b5cf6, #10b981)',
                    backgroundSize: '300% 300%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: prefersReducedMotion ? 'none' : 'gradient 4s ease infinite',
                  }}
                >
                  {t('landing.hero.titleHighlight')}
                </Box>
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: { md: '1rem', lg: '1.1rem' }, mb: 4, lineHeight: 1.6, maxWidth: 460 }}>
                {t('landing.hero.subtitle')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleStartFreeTrial}
                  endIcon={<ArrowRight size={16} />}
                  sx={{
                    px: 4, py: 1.5, fontSize: '1rem',
                    boxShadow: '0 8px 30px rgba(16, 185, 129, 0.35)',
                    ...ctaPress,
                  }}
                >
                  {t('landing.hero.ctaPrimary')}
                </Button>
                <Box
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.soldikeeper.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex', alignItems: 'center',
                    px: 2.5, py: 1.2, borderRadius: 2,
                    bgcolor: '#000', textDecoration: 'none',
                    border: '1px solid rgba(255,255,255,0.15)',
                    '&:hover': { bgcolor: '#1a1a1a', transform: 'translateY(-2px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box component="img" src="/assets/images/google-play-badge.svg" alt="Get it on Google Play" sx={{ height: 28, width: 'auto' }} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {Object.entries(t('landing.hero.trustIndicators', { returnObjects: true })).map(([key, value]) => (
                  <Chip key={key} icon={<Check size={12} />} label={value} size="small"
                    sx={{ ...glassStyle, fontSize: '0.7rem', height: 28, '& .MuiChip-icon': { ml: '4px' } }}
                  />
                ))}
              </Box>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', pr: { md: 2, lg: 4 } }}>
              <HeroDashboardMockup themeMode={themeMode} />
            </Box>
          </Box>

          {/* Floating orbs — desktop with parallax */}
          <Box sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute', top: '10%', right: '10%',
            width: 300, height: 300,
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%)',
            borderRadius: '50%', filter: 'blur(60px)',
            transform: parallax(-0.12),
            animation: prefersReducedMotion ? 'none' : 'float 8s ease-in-out infinite',
            pointerEvents: 'none',
          }} />
        </Box>

        {/* Stats Bar — desktop only with animated counters */}
        <Box sx={{
          display: { xs: 'none', md: 'block' },
          py: 3.5,
          background: themeMode === 'dark'
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(59, 130, 246, 0.08) 100%)'
            : 'linear-gradient(135deg, rgba(16, 185, 129, 0.04) 0%, rgba(59, 130, 246, 0.04) 100%)',
        }}>
          <Container maxWidth="md">
            <Grid container spacing={3}>
              {[
                { value: '6', label: t('landing.stats.languages'), color: 'primary.main' },
                { value: '€0', label: t('landing.stats.freeForever'), color: '#8b5cf6' },
                { value: '100%', label: t('landing.stats.gdprCompliant'), color: 'secondary.main' },
              ].map((stat, i) => (
                <Grid item xs={4} key={i}>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <AnimatedCounter
                      value={stat.value}
                      sx={{ fontSize: '1.75rem', fontWeight: 800, color: stat.color, lineHeight: 1.2 }}
                    />
                    <Typography color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.25 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            FEATURES — Vertical cards on mobile, grid on desktop
        ═══════════════════════════════════════════════════════════════ */}
        <Box id="features" sx={{ py: { xs: 4, md: 12 } }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 3 } }}>
            <AnimatedSection>
              <Box sx={{ textAlign: 'center', mb: { xs: 2.5, md: 8 } }}>
                <Typography
                  component="h2"
                  sx={{ fontSize: { xs: '1.3rem', md: '2.2rem' }, fontWeight: 700, mb: { xs: 0, md: 1 } }}
                >
                  {t('landing.features.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.95rem' }}>
                  {t('landing.features.subtitle')}
                </Typography>
              </Box>
            </AnimatedSection>

            {/* Mobile: vertical full-width cards | Desktop: 3-column grid */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: { xs: 2, md: 3 },
            }}>
              {features.slice(0, isMobile ? 3 : features.length).map((feature, index) => (
                <AnimatedSection key={feature.key} delay={index * 0.08}>
                  <Card
                    sx={{
                      ...glassStyle,
                      p: { xs: 2, md: 4 },
                      display: 'flex',
                      flexDirection: { xs: 'row', md: 'column' },
                      alignItems: { xs: 'center', md: 'stretch' },
                      gap: { xs: 1.5, md: 0 },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: { md: 'translateY(-8px)' },
                        boxShadow: '0 12px 40px rgba(16, 185, 129, 0.15)',
                      },
                    }}
                  >
                    <Box sx={{
                      width: { xs: 40, md: 56 }, height: { xs: 40, md: 56 },
                      minWidth: { xs: 40, md: 56 },
                      borderRadius: { xs: '10px', md: '14px' },
                      background: feature.gradient,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: { xs: 0, md: 1.5 }, color: 'white',
                      boxShadow: `0 6px 20px ${feature.gradient.includes('#10b981') ? 'rgba(16,185,129,0.3)' : 'rgba(59,130,246,0.3)'}`,
                    }}>
                      {feature.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ mb: { xs: 0, md: 0.5 }, fontWeight: 700, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                        {t(`landing.features.${feature.key}.title`)}
                      </Typography>
                      {/* Hide descriptions on mobile for cleaner look */}
                      <Typography variant="body2" color="text.secondary" sx={{
                        display: { xs: 'none', md: 'block' },
                        fontSize: '0.825rem', lineHeight: 1.55,
                      }}>
                        {t(`landing.features.${feature.key}.description`)}
                      </Typography>
                    </Box>
                  </Card>
                </AnimatedSection>
              ))}
            </Box>
          </Container>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            HOW IT WORKS — Hidden on mobile, shown on desktop
        ═══════════════════════════════════════════════════════════════ */}
        <Box sx={{
          display: { xs: 'none', md: 'block' },
          py: { xs: 6, md: 10 },
          background: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
        }}>
          <Container maxWidth="md" sx={{ px: { xs: 2.5, sm: 3 } }}>
            <AnimatedSection>
              <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                <Typography component="h2" sx={{ fontSize: { xs: '1.5rem', md: '2.2rem' }, fontWeight: 700, mb: 0.5 }}>
                  {t('landing.howItWorks.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.88rem', md: '0.9rem' } }}>
                  {t('landing.howItWorks.subtitle')}
                </Typography>
              </Box>
            </AnimatedSection>

            <Box sx={{ position: 'relative' }}>
              {/* Connecting line — mobile */}
              <Box sx={{
                display: { xs: 'block', md: 'none' },
                position: 'absolute', left: 27, top: 30, bottom: 30, width: 2,
                background: 'linear-gradient(180deg, #10b981, #3b82f6, #8b5cf6)',
                borderRadius: 1,
              }} />

              {steps.map((step, index) => {
                const gradients = [
                  'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                  'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                ];
                const colors = ['#10b981', '#3b82f6', '#8b5cf6'];
                return (
                  <AnimatedSection key={step.key} delay={index * 0.15}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: { xs: 2, md: 4 },
                        mb: index < steps.length - 1 ? { xs: 3, md: 4 } : 0,
                        alignItems: 'flex-start',
                        flexDirection: { xs: 'row', md: index % 2 === 0 ? 'row' : 'row-reverse' },
                        textAlign: { xs: 'left', md: index % 2 === 0 ? 'left' : 'right' },
                      }}
                    >
                      <Box sx={{
                        flexShrink: 0,
                        width: { xs: 52, md: 56 }, height: { xs: 52, md: 56 },
                        borderRadius: '50%',
                        background: gradients[index],
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: { xs: '1.1rem', md: '1.25rem' }, fontWeight: 800,
                        boxShadow: `0 8px 24px ${['rgba(16,185,129,0.4)', 'rgba(59,130,246,0.4)', 'rgba(139,92,246,0.4)'][index]}`,
                        position: 'relative', zIndex: 2,
                      }}>
                        {step.number}
                      </Box>
                      <Box sx={{
                        flex: 1, ...glassStyle, borderRadius: 3, p: { xs: 2, md: 3 },
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Box sx={{ color: colors[index], '& svg': { width: { xs: 20, md: 24 }, height: { xs: 20, md: 24 } } }}>
                            {step.icon}
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1.05rem' } }}>
                            {t(`landing.howItWorks.steps.${step.key}.title`)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.82rem', md: '0.825rem' }, lineHeight: 1.55 }}>
                          {t(`landing.howItWorks.steps.${step.key}.description`)}
                        </Typography>
                      </Box>
                    </Box>
                  </AnimatedSection>
                );
              })}
            </Box>
          </Container>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            PRICING — Full-width stacked on mobile, row on desktop
        ═══════════════════════════════════════════════════════════════ */}
        <Box id="pricing" sx={{ py: { xs: 4, md: 12 } }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 3 } }}>
            <AnimatedSection>
              <Box sx={{ textAlign: 'center', mb: { xs: 2.5, md: 8 } }}>
                <Typography component="h2" sx={{ fontSize: { xs: '1.3rem', md: '2.2rem' }, fontWeight: 600, mb: { xs: 0, md: 1 } }}>
                  {t('landing.pricing.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.95rem' }}>
                  {t('landing.pricing.subtitle')}
                </Typography>
              </Box>
            </AnimatedSection>

            {/* Mobile: vertical stack | Desktop: horizontal row */}
            <Box sx={{
              display: { xs: 'flex', md: 'flex' },
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 3 },
              alignItems: { xs: 'stretch', md: 'stretch' },
            }}>
              {(isMobile ? plans.filter(p => p.key === 'free' || p.key === 'premium') : plans).map((plan, index) => (
                <AnimatedSection key={plan.key} delay={index * 0.06} sx={{ flex: { md: 1 } }}>
                  <Card
                    sx={{
                      ...glassStyle,
                      p: { xs: 2.5, md: 4 },
                      position: 'relative',
                      ...(plan.popular && {
                        transform: { md: 'scale(1.05)' },
                        border: '2px solid',
                        borderColor: 'primary.main',
                        boxShadow: '0 12px 40px rgba(16, 185, 129, 0.3)',
                      }),
                    }}
                  >
                    {plan.popular && (
                      <Chip
                        label={t('landing.pricing.popular')}
                        size="small"
                        sx={{
                          position: 'absolute', top: 16, right: 16,
                          background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                          color: 'white', fontWeight: 600,
                        }}
                      />
                    )}
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                      {t(`landing.pricing.plans.${plan.key}.name`)}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 800, display: 'inline',
                          fontSize: { xs: '1.5rem', md: '1.8rem' },
                          color: plan.popular ? 'primary.main' : 'text.primary',
                        }}
                      >
                        {t(`landing.pricing.plans.${plan.key}.price`)}
                      </Typography>
                      {plan.key !== 'free' && (
                        <Typography variant="body2" component="span" color="text.secondary">
                          {t(`landing.pricing.plans.${plan.key}.period`)}
                        </Typography>
                      )}
                    </Box>
                    <List sx={{ mb: 2 }}>
                      {t(`landing.pricing.plans.${plan.key}.features`, { returnObjects: true }).map((feature, fi) => (
                        <ListItem key={fi} sx={{ py: 0.3, px: 0 }}>
                          <Check size={16} style={{ marginRight: 8, flexShrink: 0, color: theme.palette.primary.main }} />
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2', sx: { fontSize: { xs: '0.82rem', md: '0.85rem' } } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Button
                      variant={plan.popular ? 'contained' : 'outlined'}
                      fullWidth
                      onClick={handleStartFreeTrial}
                      sx={{
                        py: { xs: 1.3, md: 1 },
                        ...(plan.popular && { boxShadow: '0 8px 30px rgba(16, 185, 129, 0.35)' }),
                      }}
                    >
                      {plan.key === 'free' ? t('landing.pricing.startFree') : t('landing.pricing.startTrial')}
                    </Button>
                  </Card>
                </AnimatedSection>
              ))}
            </Box>
          </Container>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            COMPARISON — Hidden on mobile, shown on desktop
        ═══════════════════════════════════════════════════════════════ */}
        <Box sx={{
          display: { xs: 'none', md: 'block' },
          py: { xs: 6, md: 10 },
          background: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
        }}>
          <Container maxWidth="md" sx={{ px: { xs: 2.5, sm: 3 } }}>
            <AnimatedSection>
              <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
                <Typography component="h2" sx={{ fontSize: { xs: '1.5rem', md: '2.2rem' }, fontWeight: 700, mb: 0.5 }}>
                  {t('landing.comparison.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.88rem', md: '0.9rem' } }}>
                  {t('landing.comparison.subtitle')}
                </Typography>
              </Box>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <Card sx={{ ...glassStyle, overflow: 'hidden', borderRadius: 3 }}>
                {/* Header row */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1.5fr repeat(3, 1fr)', md: '2fr repeat(3, 1fr)' },
                  gap: 0,
                  p: { xs: 1.5, md: 2 },
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
                  borderBottom: '1px solid',
                  borderColor: themeMode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                }}>
                  <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.85rem' }, fontWeight: 700, color: 'text.secondary' }}>
                    {t('landing.comparison.headers.feature')}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.85rem' }, fontWeight: 700, color: 'primary.main', textAlign: 'center' }}>
                    {t('landing.comparison.headers.soldikeeper')}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.85rem' }, fontWeight: 600, color: 'text.secondary', textAlign: 'center' }}>
                    {t('landing.comparison.headers.spreadsheets')}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.85rem' }, fontWeight: 600, color: 'text.secondary', textAlign: 'center' }}>
                    {t('landing.comparison.headers.otherApps')}
                  </Typography>
                </Box>

                {/* Data rows */}
                {(t('landing.comparison.rows', { returnObjects: true }) || []).map((row, i) => (
                  <Box key={i} sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1.5fr repeat(3, 1fr)', md: '2fr repeat(3, 1fr)' },
                    gap: 0,
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 1, md: 1.2 },
                    alignItems: 'center',
                    borderBottom: i < 7 ? '1px solid' : 'none',
                    borderColor: themeMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                    '&:hover': { background: themeMode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' },
                  }}>
                    <Typography sx={{ fontSize: { xs: '0.72rem', md: '0.85rem' }, fontWeight: 500 }}>
                      {row.feature}
                    </Typography>
                    {[row.soldikeeper, row.spreadsheets, row.otherApps].map((val, j) => (
                      <Box key={j} sx={{ textAlign: 'center' }}>
                        {val === true ? (
                          <Check size={isMobile ? 16 : 18} color="#10b981" strokeWidth={3} />
                        ) : val === false ? (
                          <X size={isMobile ? 14 : 16} color={themeMode === 'dark' ? '#475569' : '#94a3b8'} />
                        ) : (
                          <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' }, color: '#f59e0b', fontWeight: 600 }}>~</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Card>
            </AnimatedSection>
          </Container>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            TESTIMONIALS — Social proof
        ═══════════════════════════════════════════════════════════════ */}
        <Box sx={{ py: { xs: 4, md: 10 } }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 3 } }}>
            <AnimatedSection>
              <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 6 } }}>
                <Typography component="h2" sx={{ fontSize: { xs: '1.3rem', md: '2.2rem' }, fontWeight: 700, mb: 0.5 }}>
                  {t('landing.testimonials.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.9rem' }}>
                  {t('landing.testimonials.subtitle')}
                </Typography>
              </Box>
            </AnimatedSection>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: { xs: 2, md: 3 },
            }}>
              {(t('landing.testimonials.reviews', { returnObjects: true }) || []).slice(0, isMobile ? 2 : 4).map((review, i) => {
                const avatarColors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b'];
                return (
                  <AnimatedSection key={i} delay={i * 0.1}>
                    <Card sx={{
                      ...glassStyle,
                      p: { xs: 2.5, md: 3 },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: { md: 'translateY(-6px)' },
                        boxShadow: `0 12px 40px ${alpha(avatarColors[i] || '#10b981', 0.15)}`,
                      },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Avatar sx={{
                          width: 40, height: 40,
                          background: `linear-gradient(135deg, ${avatarColors[i]}, ${avatarColors[(i + 1) % 4]})`,
                          fontSize: '0.9rem', fontWeight: 700,
                        }}>
                          {review.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>{review.name}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.2 }}>{review.role}</Typography>
                        </Box>
                      </Box>
                      <Rating value={review.rating || 5} readOnly size="small" sx={{ mb: 1.5, '& .MuiRating-icon': { color: '#f59e0b' } }} />
                      <Typography sx={{
                        fontSize: { xs: '0.82rem', md: '0.825rem' },
                        lineHeight: 1.6,
                        color: 'text.secondary',
                        flex: 1,
                        fontStyle: 'italic',
                      }}>
                        "{review.text}"
                      </Typography>
                    </Card>
                  </AnimatedSection>
                );
              })}
            </Box>
          </Container>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            FAQ — Hidden on mobile, shown on desktop
        ═══════════════════════════════════════════════════════════════ */}
        <Box id="faq" sx={{
          display: { xs: 'none', md: 'block' },
          py: { xs: 6, md: 10 },
          background: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
        }}>
          <Container maxWidth="md" sx={{ px: { xs: 2.5, sm: 3 } }}>
            <AnimatedSection>
              <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
                <Box sx={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 2, color: 'white',
                }}>
                  <HelpCircle size={28} />
                </Box>
                <Typography component="h2" sx={{ fontSize: { xs: '1.5rem', md: '2.2rem' }, fontWeight: 700, mb: 0.5 }}>
                  {t('landing.faq.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.88rem', md: '0.9rem' } }}>
                  {t('landing.faq.subtitle')}
                </Typography>
              </Box>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              {(t('landing.faq.items', { returnObjects: true }) || []).map((item, i) => (
                <Accordion
                  key={i}
                  expanded={faqExpanded === i}
                  onChange={(_, isExpanded) => { selectionClick(); setFaqExpanded(isExpanded ? i : false); }}
                  disableGutters
                  elevation={0}
                  sx={{
                    ...glassStyle,
                    mb: 1.5,
                    borderRadius: '12px !important',
                    '&:before': { display: 'none' },
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ChevronDown size={20} style={{
                      transition: 'transform 0.3s ease',
                      transform: faqExpanded === i ? 'rotate(180deg)' : 'rotate(0deg)',
                    }} />}
                    sx={{
                      px: { xs: 2, md: 3 },
                      py: { xs: 0.5, md: 0.8 },
                      '& .MuiAccordionSummary-expandIconWrapper': { transform: 'none !important' },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.88rem', md: '0.95rem' } }}>
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 2.5 }, pt: 0 }}>
                    <Typography sx={{
                      fontSize: { xs: '0.82rem', md: '0.85rem' },
                      lineHeight: 1.65,
                      color: 'text.secondary',
                    }}>
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AnimatedSection>
          </Container>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            FINAL CTA — Immersive on mobile
        ═══════════════════════════════════════════════════════════════ */}
        <Box sx={{
          py: { xs: 5, md: 12 },
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)',
          textAlign: 'center',
        }}>
          <Container maxWidth="md" sx={{ px: { xs: 3, sm: 3 } }}>
            <AnimatedSection>
              <Typography
                component="h2"
                sx={{ fontSize: { xs: '1.3rem', md: '2.2rem' }, fontWeight: 700, mb: 1 }}
              >
                {t('landing.finalCta.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, mb: 2, fontSize: '0.95rem' }}>
                {t('landing.finalCta.subtitle')}
              </Typography>

              {/* Aspirational tagline */}
              <Typography sx={{
                fontSize: { xs: '1.05rem', md: '1.3rem' },
                fontWeight: 600,
                mt: { xs: 1, md: 0 },
                background: 'linear-gradient(135deg, #10b981, #3b82f6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '0.03em',
              }}>
                {t('landing.finalCta.tagline')}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5, fontSize: { xs: '0.78rem', md: '0.85rem' } }}>
                {t('landing.hero.trustIndicators.noBankLogin')} • {t('landing.hero.trustIndicators.gdpr')}
              </Typography>
            </AnimatedSection>
          </Container>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            FOOTER — Minimal
        ═══════════════════════════════════════════════════════════════ */}
        <Box sx={{
          pt: { xs: 2.5, md: 4 },
          pb: { xs: showFloatingCta && isMobile ? 12 : 3, md: 4 },
          background: themeMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)',
          borderTop: '1px solid',
          borderColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2.5, sm: 3 } }}>
            <Box sx={{
              display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="img" src="/assets/images/logo.png" alt="SoldiKeeper" sx={{ height: { xs: 24, sm: 32 }, width: 'auto' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.72rem', sm: '0.8rem' } }}>
                  © {new Date().getFullYear()} SoldiKeeper. {t('footer.rights')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 }, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Typography
                  component="span"
                  onClick={() => setPrivacyDialogOpen(true)}
                  sx={{ fontSize: { xs: '0.68rem', sm: '0.7rem' }, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                >
                  {t('footer.privacy')}
                </Typography>
                <Typography
                  component="span"
                  onClick={() => setCookieDialogOpen(true)}
                  sx={{ fontSize: { xs: '0.68rem', sm: '0.7rem' }, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                >
                  {t('footer.cookie', 'Cookie Policy')}
                </Typography>
                <Typography
                  component="a"
                  href="https://www.iubenda.com/terms-and-conditions/70671443"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: { xs: '0.68rem', sm: '0.7rem' }, color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                >
                  {t('footer.terms')}
                </Typography>
                {SOCIALS.map((social) => (
                  <Typography
                    key={social.href}
                    component="a"
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: { xs: '0.68rem', sm: '0.7rem' }, color: 'text.secondary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                  >
                    {social.label}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ═══════════════════════════════════════════════════════════════
            FLOATING BOTTOM CTA BAR — Mobile only, appears after scroll
        ═══════════════════════════════════════════════════════════════ */}
        <Slide direction="up" in={showFloatingCta && isMobile} mountOnEnter unmountOnExit>
          <Box sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            px: 2,
            py: 1.5,
            pb: 'max(1.5rem, env(safe-area-inset-bottom))',
            background: themeMode === 'dark'
              ? 'rgba(15, 23, 42, 0.92)'
              : 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid',
            borderColor: themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
            boxShadow: '0 -4px 30px rgba(0,0,0,0.15)',
          }}>
            <Box sx={{ display: 'flex', gap: 1, maxWidth: 400, mx: 'auto' }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleStartFreeTrial}
                sx={{
                  py: 1.3,
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  borderRadius: 2.5,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 16px rgba(16, 185, 129, 0.35)',
                  flex: 2,
                }}
              >
                {t('landing.hero.ctaPrimary')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleSignIn}
                sx={{
                  py: 1.3,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  borderRadius: 2.5,
                  flex: 1,
                  borderWidth: 2,
                  borderColor: themeMode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                  color: 'text.primary',
                  '&:hover': { borderWidth: 2 },
                }}
              >
                {t('navigation.signIn')}
              </Button>
            </Box>
          </Box>
        </Slide>

        {/* ═══════════════════════════════════════════════════════════════
            LEGAL DIALOGS — Lazy-loaded for performance
        ═══════════════════════════════════════════════════════════════ */}
        {(privacyDialogOpen || cookieDialogOpen) && (
          <Suspense fallback={null}>
            <LegalDialogs
              privacyDialogOpen={privacyDialogOpen}
              cookieDialogOpen={cookieDialogOpen}
              setPrivacyDialogOpen={setPrivacyDialogOpen}
              setCookieDialogOpen={setCookieDialogOpen}
              policyData={policyData}
              t={t}
            />
          </Suspense>
        )}

      </Box>
    </ThemeProvider>
  );
};

export default LandingPage;
